import type { QuickFilter } from '@lens/shared';
import type { FilterLeaf } from './types';
import { isFilterApplicable } from './operators';

export type { QuickFilter };

/**
 * Комбинирование быстрых фильтров с пользовательскими — по AND (запись должна
 * пройти каждый включённый набор). Каждый набор — плоский `FilterLeaf[]` с
 * семантикой AND > OR (см. `FilterLeafSchema`), т.е. внутри уже может быть OR.
 *
 * Модель фильтров плоская (без вложенных групп), поэтому AND нескольких наборов,
 * где внутри встречается OR, раскрываем в дизъюнктивную нормальную форму (DNF):
 * `(a OR b) AND (c)` → `(a AND c) OR (b AND c)`. Плоский массив с combinator
 * ровно это и выражает: термы, разделённые `or`, внутри терма — `and`. Результат
 * без изменений уходит и на бэкенд (`additionalFilters`), и в клиентский
 * `applyFilters` — оба уже понимают эту семантику.
 */

/** Разбивает плоский фильтр в OR-термы (список AND-последовательностей). */
function toOrTerms(filters: FilterLeaf[]): FilterLeaf[][] {
  const active = filters.filter(isFilterApplicable);
  if (active.length === 0) return [];
  const terms: FilterLeaf[][] = [[]];
  active.forEach((leaf, i) => {
    if (i === 0 || leaf.combinator !== 'or') {
      terms[terms.length - 1]!.push(leaf);
    } else {
      terms.push([leaf]);
    }
  });
  return terms;
}

/**
 * Порог защиты от комбинаторного взрыва DNF. Быстрые фильтры почти всегда —
 * чистый AND (один терм), поэтому реальные значения близки к 1. Если несколько
 * наборов с OR дают декартово произведение больше порога — отдаём наборы как
 * простую конкатенацию по AND (тогда OR внутри теряет строгую скобочность, но
 * не роняет UI и не шлёт гигантский WHERE). На практике не достигается.
 */
const MAX_DNF_TERMS = 256;

/**
 * AND нескольких фильтр-наборов с раскрытием в DNF. Пустые наборы (нет применимых
 * условий) не ограничивают выборку и отбрасываются. Возвращает плоский
 * `FilterLeaf[]` (combinator: `or` начинает новый терм, внутри терма — `and`).
 */
export function combineFilterGroupsAnd(groups: FilterLeaf[][]): FilterLeaf[] {
  const termSets = groups.map(toOrTerms).filter((t) => t.length > 0);
  if (termSets.length === 0) return [];

  // Быстрый путь: все наборы — один терм (чистый AND) → конкатенация в один AND-терм.
  if (termSets.every((t) => t.length === 1)) {
    return serialize([termSets.flatMap((t) => t[0]!)]);
  }

  const totalTerms = termSets.reduce((acc, t) => acc * t.length, 1);
  if (totalTerms > MAX_DNF_TERMS) {
    // Фолбэк: конкатенируем все условия по AND (см. коммент к MAX_DNF_TERMS).
    return serialize([termSets.flatMap((t) => t.flat())]);
  }

  // Декартово произведение термов по AND.
  let product: FilterLeaf[][] = termSets[0]!.map((term) => [...term]);
  for (let k = 1; k < termSets.length; k++) {
    const next: FilterLeaf[][] = [];
    for (const acc of product) {
      for (const term of termSets[k]!) {
        next.push([...acc, ...term]);
      }
    }
    product = next;
  }
  return serialize(product);
}

/**
 * Сериализует DNF (список AND-термов) в плоский массив с combinator'ами:
 * первый leaf каждого терма (кроме самого первого) получает `or`, остальные `and`.
 */
function serialize(terms: FilterLeaf[][]): FilterLeaf[] {
  const out: FilterLeaf[] = [];
  terms.forEach((term, ti) => {
    term.forEach((leaf, li) => {
      const combinator: 'and' | 'or' = ti > 0 && li === 0 ? 'or' : 'and';
      out.push({ fieldSlug: leaf.fieldSlug, operator: leaf.operator, value: leaf.value, combinator });
    });
  });
  return out;
}

/** Определения быстрых фильтров из view.config (безопасное чтение произвольного JSON). */
export function extractQuickFilters(config: unknown): QuickFilter[] {
  const raw = (config as { quickFilters?: unknown } | null)?.quickFilters;
  if (!Array.isArray(raw)) return [];
  const out: QuickFilter[] = [];
  for (const q of raw) {
    if (!q || typeof q !== 'object') continue;
    const o = q as Record<string, unknown>;
    if (typeof o.id !== 'string' || typeof o.name !== 'string') continue;
    out.push({
      id: o.id,
      name: o.name,
      color: typeof o.color === 'string' ? o.color : undefined,
      filters: Array.isArray(o.filters) ? (o.filters as FilterLeaf[]) : [],
    });
  }
  return out;
}
